import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';

import { TextField } from '@mui/material';

import { TrendingPlace } from '~/components/Content/TrendingPlaces/Columns';

type Props = {
  row: TrendingPlace;
  onSave: (row: TrendingPlace, manualPosition?: number | null) => Promise<boolean>;
};

function ManualPositionCell({ row, onSave }: Props) {
  const originalValue = typeof row.manualPosition === 'number' ? String(row.manualPosition) : '';
  const [value, setValue] = useState(originalValue);
  const [isSaving, setIsSaving] = useState(false);
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setValue(originalValue);
    }
  }, [originalValue]);

  const handleSave = async () => {
    const manualPosition = value === '' ? null : Number(value);

    if (manualPosition !== null && (!Number.isInteger(manualPosition) || manualPosition < 1)) {
      setValue(originalValue);
      return;
    }

    const originalPosition = originalValue === '' ? null : Number(originalValue);
    if (manualPosition === originalPosition) {
      return;
    }

    try {
      setIsSaving(true);
      const saved = await onSave(row, manualPosition);
      if (!saved) {
        setValue(originalValue);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  return (
    <TextField
      aria-label={`Manual position for ${row.placeName}`}
      disabled={isSaving}
      inputProps={{ min: 1, step: 1 }}
      onBlur={() => {
        isFocused.current = false;
        void handleSave();
      }}
      onChange={(event) => setValue(event.target.value)}
      onFocus={() => {
        isFocused.current = true;
      }}
      onKeyDown={handleKeyDown}
      size="small"
      type="number"
      value={value}
      variant="outlined"
    />
  );
}

export default ManualPositionCell;
